import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Menu, DollarSign, MapPin, Filter, ChevronRight, Package, Bike, ShoppingBag, PersonStanding, Bell, Loader2, Radio, Navigation } from 'lucide-react';
import { GoogleSdkMap } from '../map/GoogleSdkMap';
import { dbService } from '../../services/firebase-db.service';
import { walletService } from '../../services/wallet.service';
import { auth } from '../../config/firebase.config';

interface Job {
  id: string;
  pickupName: string;
  pickupAddress: string;
  pickupPhone?: string;
  pickupLocation?: { lat: number; lng: number };
  deliveryName: string;
  deliveryAddress: string;
  deliveryLocation?: { lat: number; lng: number };
  distance: string;
  estimatedEarnings: string;
  estimatedTime: string;
  packageType: string;
  items?: string[];
  customerName: string;
  customerPhone: string;
  status: 'available' | 'accepted' | 'picked-up' | 'completed';
  type: 'food' | 'parcel' | 'errand';
  fleetType: 'rider' | 'bicycle' | 'leg-fleet';
  notes?: string;
}

interface HomeScreenProps {
  onJobSelect: (jobId: string, cached?: {
    pickupLocation?: { lat: number; lng: number };
    deliveryLocation?: { lat: number; lng: number };
    pickupAddress?: string;
    deliveryAddress?: string;
    earnings?: number;
    pickupName?: string;
    customerName?: string;
    customerPhone?: string;
    pickupPhone?: string;
  }) => void;
  onResumeActiveJob?: () => void;
  activeJobId?: string | null;
  onNavigateToEarnings: () => void;
  onNavigateToProfile: () => void;
  onNavigateToShift: () => void;
  onNavigateToHistory: () => void;
  onNavigateToNotifications?: () => void;
  riderFleetType?: 'rider' | 'bicycle' | 'leg-fleet';
  unreadNotifications?: number;
}

export function HomeScreen({
  onJobSelect,
  onResumeActiveJob,
  activeJobId = null,
  onNavigateToEarnings,
  onNavigateToProfile,
  onNavigateToShift,
  onNavigateToHistory,
  onNavigateToNotifications,
  riderFleetType = 'rider',
  unreadNotifications = 0
}: HomeScreenProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedFleetTypes, setSelectedFleetTypes] = useState<string[]>([]);
  const [radiusFilter, setRadiusFilter] = useState('all');
  const [isOnline, setIsOnline] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [passedJobIds, setPassedJobIds] = useState<Set<string>>(new Set());
  const [todayEarningsAmount, setTodayEarningsAmount] = useState('₦0');
  const [riderLocation, setRiderLocation] = useState<{ lat: number; lng: number } | null>(null);
  const jobsListSignatureRef = useRef('');

  const transformAvailableJobs = useCallback((availableJobs: Awaited<ReturnType<typeof dbService.getAvailableJobs>>) => {
    return availableJobs.map(job => ({
      id: job.id,
      pickupName: job.pickupName || job.pickupAddress.split(',')[0] || 'Pickup Location',
      pickupAddress: job.pickupAddress,
      pickupPhone: job.pickupPhone,
      pickupLocation: job.pickupLocation,
      deliveryName: job.deliveryAddress.split(',')[0] || 'Delivery Location',
      deliveryAddress: job.deliveryAddress,
      deliveryLocation: job.deliveryLocation,
      distance: job.distance,
      estimatedEarnings: `₦${job.earnings}`,
      estimatedTime: job.estimatedTime,
      packageType: job.packageType || 'Package',
      items: Array.isArray(job.items) ? job.items : [],
      customerName: job.customerName || 'Customer',
      customerPhone: job.customerPhone || '',
      status: 'available' as const,
      type: (job.packageType?.toLowerCase().includes('food') ? 'food' :
             job.packageType?.toLowerCase().includes('errand') ? 'errand' : 'parcel') as 'food' | 'parcel' | 'errand',
      fleetType: riderFleetType,
      notes: job.notes || ''
    }));
  }, [riderFleetType]);

  // Load rider status and earnings on mount (jobs load when online)
  useEffect(() => {
    loadRiderStatus();
    loadTodayEarnings();
  }, []);

  useEffect(() => {
    if (!isOnline || !('geolocation' in navigator)) {
      setRiderLocation(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setRiderLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {},
      { enableHighAccuracy: true, timeout: 15000 }
    );

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setRiderLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [isOnline]);

  const loadRiderStatus = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const profile = await dbService.getRiderProfile(user.uid);
      if (profile) setIsOnline(profile.isOnline ?? false);
    } catch (_) {}
  };

  const loadTodayEarnings = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const wallet = await walletService.getWalletBalance(user.uid);
      const todayTotal = wallet.todayEarnings ?? 0;
      setTodayEarningsAmount(`₦${todayTotal.toLocaleString()}`);
    } catch (_) {}
  };

  const handleToggleOnline = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const newStatus = !isOnline;
    setIsOnline(newStatus);

    try {
      if (newStatus) {
        await dbService.startShift(user.uid);
      } else {
        await dbService.goOffline(user.uid);
      }
    } catch (err) {
      console.error('Failed to update online status:', err);
      try {
        const profile = await dbService.getRiderProfile(user.uid);
        if (profile?.isOnline === newStatus) {
          return;
        }
      } catch (_) {}
      setIsOnline(!newStatus);
      alert('Could not change online status. Please check your connection and try again.');
    }
  };

  // Load jobs when going online + realtime Firestore listener
  useEffect(() => {
    if (!isOnline) {
      setJobs([]);
      setLoading(false);
      return;
    }

    const vehicleType: 'bike' | 'bicycle' | 'leg' =
      riderFleetType === 'rider' ? 'bike' :
      riderFleetType === 'leg-fleet' ? 'leg' :
      'bicycle';

    setLoading(true);
    const unsub = dbService.subscribeToAvailableJobs(50, vehicleType, (availableJobs) => {
      const signature = availableJobs
        .map((job) => `${job.id}:${job.earnings}:${job.distance}`)
        .sort()
        .join('|');
      if (signature === jobsListSignatureRef.current) {
        setLoading(false);
        return;
      }
      jobsListSignatureRef.current = signature;

      const transformedJobs = transformAvailableJobs(availableJobs);
      setJobs(transformedJobs);
      setLoading(false);
      console.log(`✅ Loaded ${transformedJobs.length} jobs from Firebase`);
    });

    return () => unsub();
  }, [isOnline, riderFleetType, transformAvailableJobs]);

  const loadJobs = async (silent = false) => {
    const user = auth.currentUser;
    if (!user) {
      setJobs([]);
      setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);

      if (!isOnline) {
        setJobs([]);
        return;
      }

      const vehicleType: 'bike' | 'bicycle' | 'leg' = 
        riderFleetType === 'rider' ? 'bike' : 
        riderFleetType === 'leg-fleet' ? 'leg' : 
        'bicycle';
      const availableJobs = await dbService.getAvailableJobs(50, vehicleType);
      
      // Transform Firebase jobs to match local Job interface
      const transformedJobs = availableJobs.map(job => ({
        id: job.id,
        pickupName: job.pickupName || job.pickupAddress.split(',')[0] || 'Pickup Location',
        pickupAddress: job.pickupAddress,
        pickupPhone: job.pickupPhone,
        pickupLocation: job.pickupLocation,
        deliveryName: job.deliveryAddress.split(',')[0] || 'Delivery Location',
        deliveryAddress: job.deliveryAddress,
        deliveryLocation: job.deliveryLocation,
        distance: job.distance,
        estimatedEarnings: `₦${job.earnings}`,
        estimatedTime: job.estimatedTime,
        packageType: job.packageType || 'Package',
        items: Array.isArray(job.items) ? job.items : [],
        customerName: job.customerName || 'Customer',
        customerPhone: job.customerPhone || '',
        status: 'available' as const,
        type: (job.packageType?.toLowerCase().includes('food') ? 'food' : 
               job.packageType?.toLowerCase().includes('errand') ? 'errand' : 'parcel') as 'food' | 'parcel' | 'errand',
        fleetType: riderFleetType,
        notes: job.notes || ''
      }));
      
      setJobs(transformedJobs);
      console.log(`✅ Loaded ${transformedJobs.length} jobs from Firebase`);
    } catch (error) {
      console.error('❌ Failed to load jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const todayEarnings = todayEarningsAmount;
  const availableJobs = jobs.filter(job => job.status === 'available' && !passedJobIds.has(job.id));

  // Filter jobs based on rider's fleet type and distance suitability
  const getDistanceInKm = (distanceStr: string): number => {
    const match = distanceStr.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : 0;
  };

  const isJobSuitableForFleetType = (job: Job): boolean => {
    const distance = getDistanceInKm(job.distance);
    
    // Distance-based filtering:
    // ≤ 1.5km: leg, bicycle, bike
    // > 1.5km and ≤ 4km: bicycle, bike
    // > 4km: bike only
    
    if (distance <= 1.5) {
      return true; // All fleet types can take jobs ≤ 1.5km
    } else if (distance <= 4) {
      return riderFleetType === 'bicycle' || riderFleetType === 'rider';
    } else {
      return riderFleetType === 'rider'; // Only bikes for > 4km
    }
  };

  const filteredJobs = availableJobs.filter(job => {
    // Fleet type suitability check (based on distance)
    if (!isJobSuitableForFleetType(job)) {
      return false;
    }
    
    // User-selected type filters
    if (selectedTypes.length > 0 && !selectedTypes.includes(job.type)) {
      return false;
    }
    
    // User-selected fleet type filters
    if (selectedFleetTypes.length > 0 && !selectedFleetTypes.includes(job.fleetType)) {
      return false;
    }
    
    return true;
  });

  const toggleTypeFilter = (type: string) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const toggleFleetTypeFilter = (fleetType: string) => {
    setSelectedFleetTypes(prev => 
      prev.includes(fleetType) 
        ? prev.filter(t => t !== fleetType)
        : [...prev, fleetType]
    );
  };

  const getTypeBadge = (type: string) => {
    const configs = {
      food: { label: 'Food', bg: 'bg-orange-100', text: 'text-orange-700', icon: ShoppingBag },
      parcel: { label: 'Parcel', bg: 'bg-blue-100', text: 'text-blue-700', icon: Package },
      errand: { label: 'Errand', bg: 'bg-purple-100', text: 'text-purple-700', icon: Bike }
    };
    return configs[type as keyof typeof configs] || configs.parcel;
  };

  const getFleetTypeInfo = (fleetType: string) => {
    const configs = {
      'rider': { label: 'Rider', bg: 'bg-[#E0001B]/10', text: 'text-[#E0001B]', icon: Bike },
      'bicycle': { label: 'Bicycle', bg: 'bg-[#0066CC]/10', text: 'text-[#0066CC]', icon: Bike },
      'leg-fleet': { label: 'Leg Fleet', bg: 'bg-[#00B277]/10', text: 'text-[#00B277]', icon: PersonStanding }
    };
    return configs[fleetType as keyof typeof configs] || configs.rider;
  };

  const previewJob = filteredJobs[0];
  const mapPickupLocation = useMemo(
    () => previewJob?.pickupLocation,
    [previewJob?.id, previewJob?.pickupLocation?.lat, previewJob?.pickupLocation?.lng]
  );
  const mapDeliveryLocation = useMemo(
    () => previewJob?.deliveryLocation,
    [previewJob?.id, previewJob?.deliveryLocation?.lat, previewJob?.deliveryLocation?.lng]
  );
  const nearbyJobsForMap = useMemo(
    () =>
      filteredJobs
        .filter((job) => job.pickupLocation?.lat && job.pickupLocation?.lng)
        .map((job) => ({
          id: job.id,
          lat: job.pickupLocation!.lat,
          lng: job.pickupLocation!.lng,
          label: job.pickupName,
        })),
    [filteredJobs]
  );
  const showMapRoute = filteredJobs.length > 0;

  return (
    <div className="absolute inset-0 bg-gray-50 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setShowMenu(!showMenu)} 
            className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <Menu className="w-6 h-6 text-[#111111]" />
          </button>
          
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-[#00B277]' : 'bg-[#6B6B6B]'}`} />
            <span className="text-[14px] text-[#111111]">
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          
          <button 
            onClick={onNavigateToEarnings}
            className="flex items-center gap-2 px-3 py-2 bg-[#00B277]/10 rounded-lg"
          >
            <DollarSign className="w-4 h-4 text-[#00B277]" />
            <span className="text-[14px] text-[#00B277]">{todayEarnings}</span>
          </button>
        </div>
      </div>

      {activeJobId && onResumeActiveJob && (
        <button
          type="button"
          onClick={onResumeActiveJob}
          className="mx-4 mt-3 mb-1 flex items-center gap-3 rounded-2xl bg-[#111111] px-4 py-3 text-left shadow-lg"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E0001B]">
            <Navigation className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-white">Active delivery in progress</p>
            <p className="truncate text-[12px] text-white/70">#{activeJobId} — tap to resume navigation</p>
          </div>
          <ChevronRight className="h-5 w-5 flex-shrink-0 text-white/60" />
        </button>
      )}

      {/* Dropdown Menu */}
      {showMenu && (
        <div className="absolute top-14 left-4 bg-white rounded-lg shadow-lg border border-gray-200 z-50 w-48">
          <button
            onClick={() => {
              setShowMenu(false);
              onNavigateToProfile();
            }}
            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            Profile
          </button>
          <button
            onClick={() => {
              setShowMenu(false);
              onNavigateToShift();
            }}
            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            Shift Management
          </button>
          <button
            onClick={() => {
              setShowMenu(false);
              onNavigateToHistory();
            }}
            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            History
          </button>
          {onNavigateToNotifications && (
            <button
              onClick={() => {
                setShowMenu(false);
                onNavigateToNotifications();
              }}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
            >
              <Bell className="w-4 h-4 text-[#E0001B]" />
              Notifications
              {unreadNotifications > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded bg-[#E0001B] text-white text-[10px] font-bold">
                  {unreadNotifications}
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Map — premium style, rider marker always visible when online */}
      <div className="h-[45vh] relative">
        <GoogleSdkMap
          currentLocation={riderLocation || undefined}
          pickupLocation={mapPickupLocation}
          deliveryLocation={mapDeliveryLocation}
          nearbyJobs={nearbyJobsForMap}
          showRoute={showMapRoute}
          followRider={isOnline}
          compact
          zoom={riderLocation ? 15 : 14}
        />

        {isOnline && (
          <motion.div
            className="absolute bottom-4 left-4 right-4 flex justify-center pointer-events-none"
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
          >
            {filteredJobs.length === 0 ? (
              <div className="bg-[#111111]/90 backdrop-blur-md text-white px-5 py-3 rounded-2xl shadow-xl flex items-center gap-3 max-w-sm">
                <motion.div
                  className="w-9 h-9 rounded-full bg-[#E0001B]/20 flex items-center justify-center"
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <Radio className="w-4 h-4 text-[#E0001B]" />
                </motion.div>
                <div>
                  <p className="text-[13px] font-semibold">Scanning for jobs nearby</p>
                  <p className="text-[11px] text-white/70">
                    {riderLocation ? 'You are live on the map' : 'Getting your location...'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white/95 backdrop-blur px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 border border-gray-100">
                <MapPin className="w-4 h-4 text-[#E0001B]" />
                <span className="text-[14px] text-[#111111] font-medium">
                  {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} nearby
                </span>
                <ChevronRight className="w-4 h-4 text-[#6B6B6B]" />
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Jobs List */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {/* Filter Bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
          <div className="flex items-center gap-2 overflow-x-auto">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 h-10 rounded-full border flex items-center gap-2 flex-shrink-0 transition-colors ${
                showFilters ? 'bg-[#E0001B] text-white border-[#E0001B]' : 'border-gray-300 text-[#111111] hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            
            {/* Fleet Type Filters */}
            {['rider', 'bicycle', 'leg-fleet'].map(fleetType => {
              const fleetInfo = getFleetTypeInfo(fleetType);
              const FleetIcon = fleetInfo.icon;
              return (
                <button
                  key={fleetType}
                  onClick={() => toggleFleetTypeFilter(fleetType)}
                  className={`px-4 h-10 rounded-full border flex items-center gap-2 flex-shrink-0 transition-colors ${
                    selectedFleetTypes.includes(fleetType)
                      ? `${fleetInfo.bg} ${fleetInfo.text} border-transparent`
                      : 'border-gray-300 text-[#111111] hover:bg-gray-50'
                  }`}
                >
                  <FleetIcon className="w-4 h-4" />
                  {fleetInfo.label}
                </button>
              );
            })}
            
            {['food', 'parcel', 'errand'].map(type => {
              const badge = getTypeBadge(type);
              const Icon = badge.icon;
              return (
                <button
                  key={type}
                  onClick={() => toggleTypeFilter(type)}
                  className={`px-4 h-10 rounded-full border flex items-center gap-2 flex-shrink-0 transition-colors ${
                    selectedTypes.includes(type)
                      ? `${badge.bg} ${badge.text} border-transparent`
                      : 'border-gray-300 text-[#111111] hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {badge.label}
                </button>
              );
            })}
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="space-y-3">
                <div>
                  <label className="block text-[12px] text-[#6B6B6B] mb-2">Radius</label>
                  <div className="flex gap-2">
                    {['all', '2km', '5km', '10km'].map(radius => (
                      <button
                        key={radius}
                        onClick={() => setRadiusFilter(radius)}
                        className={`px-3 h-8 rounded-lg text-[14px] transition-colors ${
                          radiusFilter === radius
                            ? 'bg-[#E0001B] text-white'
                            : 'bg-gray-100 text-[#111111] hover:bg-gray-200'
                        }`}
                      >
                        {radius === 'all' ? 'All' : radius}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Jobs Cards */}
        <div className="p-4 space-y-3">
          {filteredJobs.length > 0 ? (
            filteredJobs.map(job => {
              const badge = getTypeBadge(job.type);
              const Icon = badge.icon;
              const fleetInfo = getFleetTypeInfo(job.fleetType);
              const FleetIcon = fleetInfo.icon;
              
              return (
                <div key={job.id} className="bg-white rounded-[12px] shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-[#E0001B]/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#E0001B]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] ${badge.bg} ${badge.text}`}>
                              {badge.label}
                            </span>
                            <div className={`flex items-center gap-1 px-2 py-0.5 rounded ${fleetInfo.bg}`}>
                              <FleetIcon className={`w-3 h-3 ${fleetInfo.text}`} />
                              <span className={`text-[10px] ${fleetInfo.text}`}>{fleetInfo.label}</span>
                            </div>
                          </div>
                          <span className="text-[12px] text-[#6B6B6B]">#{job.id}</span>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          onJobSelect(job.id, {
                            pickupLocation: job.pickupLocation,
                            deliveryLocation: job.deliveryLocation,
                            pickupAddress: job.pickupAddress,
                            deliveryAddress: job.deliveryAddress,
                            pickupName: job.pickupName,
                            customerName: job.customerName,
                            customerPhone: job.customerPhone,
                            pickupPhone: job.pickupPhone,
                          })
                        }
                        className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <ChevronRight className="w-5 h-5 text-[#6B6B6B]" />
                      </button>
                    </div>

                    {job.items && job.items.length > 0 ? (
                      <div className="mb-3 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                        <p className="text-[11px] font-medium text-amber-900 mb-0.5">Contents to pick</p>
                        <p className="text-[13px] text-[#111111] line-clamp-2">
                          {job.items.slice(0, 3).join(', ')}
                          {job.items.length > 3 ? ` +${job.items.length - 3} more` : ''}
                        </p>
                      </div>
                    ) : null}

                    {/* Route */}
                    <div className="space-y-2 mb-3">
                      <div className="flex gap-3">
                        <div className="flex flex-col items-center pt-1">
                          <div className="w-3 h-3 rounded-full border-2 border-[#E0001B]" />
                          <div className="w-0.5 h-6 bg-[#E0001B]/30" />
                          <div className="w-3 h-3 rounded-full bg-[#00B277]" />
                        </div>
                        <div className="flex-1 space-y-4 min-w-0">
                          <div>
                            <p className="text-[12px] text-[#6B6B6B]">{job.pickupName}</p>
                            <p className="text-[14px] text-[#111111] truncate">{job.pickupAddress}</p>
                          </div>
                          <div>
                            <p className="text-[12px] text-[#6B6B6B]">{job.deliveryName}</p>
                            <p className="text-[14px] text-[#111111] truncate">{job.deliveryAddress}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-4">
                        <span className="text-[14px] text-[#6B6B6B]">{job.distance}</span>
                        <span className="text-[14px] text-[#6B6B6B]">{job.estimatedTime}</span>
                      </div>
                      <span className="text-[18px] text-[#00B277]">{job.estimatedEarnings}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => setPassedJobIds(prev => new Set([...prev, job.id]))}
                        className="flex-1 h-10 rounded-[12px] border border-gray-300 text-[#6B6B6B] hover:bg-gray-50 transition-colors"
                      >
                        Pass
                      </button>
                      <button
                        onClick={() =>
                          onJobSelect(job.id, {
                            pickupLocation: job.pickupLocation,
                            deliveryLocation: job.deliveryLocation,
                            pickupAddress: job.pickupAddress,
                            deliveryAddress: job.deliveryAddress,
                            pickupName: job.pickupName,
                            customerName: job.customerName,
                            customerPhone: job.customerPhone,
                            pickupPhone: job.pickupPhone,
                          })
                        }
                        className="flex-1 h-10 rounded-[12px] bg-[#00B277] text-white hover:bg-[#009B65] transition-colors shadow-sm"
                      >
                        Accept
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <Package className="w-8 h-8 text-[#6B6B6B]" />
              </div>
              <p className="text-[#111111] mb-1">No jobs available</p>
              <p className="text-[14px] text-[#6B6B6B] mb-4">
                {selectedTypes.length > 0 ? 'Try adjusting your filters' : 'Check back soon for new deliveries'}
              </p>
              {!loading && (
                <button
                  onClick={() => loadJobs()}
                  className="px-6 py-2 bg-[#E0001B] text-white rounded-[12px] hover:bg-[#C00017] transition-colors inline-flex items-center gap-2"
                >
                  <Loader2 className="w-4 h-4" />
                  Refresh Jobs
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating Online/Offline Button */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
        <button
          onClick={handleToggleOnline}
          className={`px-8 h-14 rounded-full shadow-lg flex items-center gap-3 transition-all ${
            isOnline
              ? 'bg-[#E0001B] text-white hover:bg-[#C00017]'
              : 'bg-white text-[#111111] border-2 border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-white' : 'bg-[#6B6B6B]'}`} />
          <span className="text-[16px]">{isOnline ? 'Go Offline' : 'Go Online'}</span>
        </button>
      </div>
    </div>
  );
}